import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarCheck, MessageSquareWarning, Utensils, ArrowRightLeft, UserPlus, Bell } from "lucide-react";
import Shell from "@/components/Shell";
import { Panel, Btn, Field, Input, TextArea, Select, StatusBadge, AllergyFlag } from "@/components/kit";
import { GuestCreate } from "@/components/GuestCreate";
import { NotificationsPanel } from "@/components/NotificationsPanel";
import api, { formatApiError } from "@/lib/api";

const NAV = [
  { key: "dashboard", label: "Dashboard", icon: Bell },
  { key: "bookings", label: "Booking Lifecycle", icon: CalendarCheck },
  { key: "triage", label: "Complaint Triage", icon: MessageSquareWarning },
  { key: "services", label: "Services", icon: Utensils },
  { key: "guests", label: "Guests", icon: UserPlus },
  { key: "handover", label: "Handover", icon: ArrowRightLeft },
];

export default function ReceptionPortal() {
  const [active, setActive] = useState("dashboard");
  const [data, setData] = useState({ bookings: [], complaints: [], services: [], departments: [], locations: [], staff: [], handovers: [] });
  const load = () => {
    Promise.all([
      api.get("/bookings"), api.get("/complaints"), api.get("/services"),
      api.get("/departments"), api.get("/locations"), api.get("/staff"), api.get("/handovers"),
    ]).then(([b, c, s, d, l, st, h]) =>
      setData({ bookings: b.data, complaints: c.data, services: s.data, departments: d.data, locations: l.data, staff: st.data, handovers: h.data })
    ).catch(() => {});
  };
  useEffect(load, []);

  return (
    <Shell title="Reception" subtitle="Manage bookings and triage guest requests" nav={NAV} active={active} onNav={setActive}>
      {active === "dashboard" && <NotificationsPanel title="Reception updates" intro="New reservation requests and guest requests appear here." />}
      {active === "bookings" && <BookingLifecycle bookings={data.bookings} reload={load} />}
      {active === "triage" && <Triage complaints={data.complaints} departments={data.departments} staff={data.staff} reload={load} />}
      {active === "services" && <Services data={data} reload={load} />}
      {active === "guests" && <GuestCreate />}
      {active === "handover" && <HandoverList items={data.handovers} locations={data.locations} reload={load} />}
    </Shell>
  );
}

function BookingLifecycle({ bookings, reload }) {
  const act = async (b, status) => {
    try { await api.patch(`/bookings/${b.id}/status`, { status }); toast.success(`Booking ${status}`); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  if (!bookings.length) return <Empty text="No bookings yet." />;
  return (
    <div className="overflow-x-auto rounded-xl border border-[#2E1256] bg-[#14052E]">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs uppercase tracking-wider text-purple-300 bg-[#1F0842]">
          {["Guest", "Service", "When", "Party", "Dept", "Allergy", "Status", "Actions"].map((h) => <th key={h} className="p-3">{h}</th>)}
        </tr></thead>
        <tbody>
          {bookings.map((b) => (
            <tr key={b.id} className="border-t border-[#230948] hover:bg-[#1E0A3E]" data-testid={`rbooking-${b.id}`}>
              <td className="p-3 text-purple-100">{b.guest_name}</td>
              <td className="p-3 text-purple-100">{b.service_name}</td>
              <td className="p-3 text-purple-300">{b.date} {b.time}</td>
              <td className="p-3 text-purple-300">{b.party_size}</td>
              <td className="p-3 text-purple-300">{b.department_name}</td>
              <td className="p-3">{b.allergy_info ? <AllergyFlag info={b.allergy_info} /> : <span className="text-purple-500">—</span>}</td>
              <td className="p-3"><StatusBadge status={b.status} /></td>
              <td className="p-3">
                {b.status === "pending" && (
                  <div className="flex gap-1.5">
                    <Btn variant="secondary" className="!px-3 !py-1.5" data-testid={`approve-${b.id}`} onClick={() => act(b, "approved")}>Approve</Btn>
                    <Btn variant="danger" data-testid={`reject-${b.id}`} onClick={() => act(b, "rejected")}>Reject</Btn>
                  </div>
                )}
                {b.status === "approved" && <Btn className="!px-3 !py-1.5" data-testid={`rcomplete-${b.id}`} onClick={() => act(b, "completed")}>Complete</Btn>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Triage({ complaints, departments, staff, reload }) {
  const [sel, setSel] = useState({});
  const acknowledge = async (c) => {
    try { await api.patch(`/complaints/${c.id}/acknowledge`); toast.success("Guest acknowledged by email"); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const assign = async (c) => {
    const s = sel[c.id] || {};
    if (!s.department_id) return toast.error("Pick a department");
    try { await api.patch(`/complaints/${c.id}/assign`, { department_id: s.department_id, staff_id: s.staff_id || null }); toast.success("Routed to department"); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  if (!complaints.length) return <Empty text="No complaints to triage." />;
  return (
    <div className="space-y-4">
      {complaints.map((c) => (
        <Panel key={c.id} className="p-5" data-testid={`triage-${c.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2"><h3 className="font-head font-bold text-white">{c.subject}</h3><code className="text-[11px] text-amber-300">{c.reference}</code></div>
              <p className="text-xs text-purple-400 mt-0.5">{c.location_name} · {c.category} · {c.guest_name}</p>
            </div>
            <StatusBadge status={c.status} />
          </div>
          <p className="text-sm text-purple-200/80 mt-2">{c.description}</p>
          <p className="text-xs mt-2">{c.acknowledged ? <span className="text-sky-300">✓ Guest acknowledged</span> : <span className="text-amber-300">Not yet acknowledged</span>}{c.department_name && <span className="text-purple-300"> · Routed to {c.department_name}{c.assigned_to_name ? ` (${c.assigned_to_name})` : ""}</span>}</p>
          <div className="flex flex-wrap items-end gap-2 mt-4">
            {!c.acknowledged && <Btn variant="secondary" data-testid={`ack-comp-${c.id}`} onClick={() => acknowledge(c)}>Acknowledge guest</Btn>}
            <div className="min-w-[150px]"><Select data-testid={`assign-dept-${c.id}`} value={sel[c.id]?.department_id || ""} onChange={(e) => setSel({ ...sel, [c.id]: { ...sel[c.id], department_id: e.target.value } })}>
              <option value="">Department…</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </Select></div>
            <div className="min-w-[150px]"><Select data-testid={`assign-staff-${c.id}`} value={sel[c.id]?.staff_id || ""} onChange={(e) => setSel({ ...sel, [c.id]: { ...sel[c.id], staff_id: e.target.value } })}>
              <option value="">Staff (optional)…</option>
              {staff.filter((s) => s.department_id === sel[c.id]?.department_id).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select></div>
            <Btn data-testid={`assign-${c.id}`} onClick={() => assign(c)}>Route</Btn>
          </div>
        </Panel>
      ))}
    </div>
  );
}

function Services({ data, reload }) {
  const [form, setForm] = useState({ name: "", location_id: "", department_id: "", description: "", capacity: 20, duration_min: 60, food_related: false });
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/services", { ...form, capacity: Number(form.capacity), duration_min: Number(form.duration_min) }); toast.success("Service added"); setForm({ ...form, name: "", description: "" }); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deptsForLoc = data.departments.filter((d) => d.location_id === form.location_id);
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Panel className="p-6">
        <h3 className="font-head font-bold text-white mb-4">Add a service</h3>
        <form onSubmit={add} className="space-y-3">
          <Field label="Location"><Select data-testid="svc-location" required value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value, department_id: "" })}><option value="">Select…</option>{data.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</Select></Field>
          <Field label="Department"><Select data-testid="svc-dept" required value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })}><option value="">Select…</option>{deptsForLoc.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</Select></Field>
          <Field label="Name"><Input data-testid="svc-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Description"><Input data-testid="svc-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Capacity"><Input data-testid="svc-cap" type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} /></Field>
            <Field label="Duration (min)"><Input data-testid="svc-dur" type="number" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} /></Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-purple-200"><input data-testid="svc-food" type="checkbox" checked={form.food_related} onChange={(e) => setForm({ ...form, food_related: e.target.checked })} /> Food related (requires allergy handling)</label>
          <Btn type="submit" data-testid="svc-add">Add service</Btn>
        </form>
      </Panel>
      <div className="space-y-3">
        {data.services.map((s) => (
          <Panel key={s.id} className="p-4" data-testid={`svc-${s.id}`}>
            <div className="flex justify-between"><h4 className="font-bold text-white">{s.name}</h4>{s.food_related && <span className="text-xs text-rose-300">food</span>}</div>
            <p className="text-xs text-purple-400">{s.department_name} · cap {s.capacity} · {s.duration_min}min</p>
          </Panel>
        ))}
      </div>
    </div>
  );
}

function HandoverList({ items, locations, reload }) {
  const [note, setNote] = useState("");
  const [loc, setLoc] = useState("");
  const add = async () => {
    if (!note.trim() || !loc) return toast.error("Location and note required");
    try { await api.post("/handovers", { location_id: loc, note }); setNote(""); toast.success("Posted"); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4 max-w-2xl">
      <Panel className="p-5 space-y-3">
        <Field label="Location"><Select data-testid="rh-loc" value={loc} onChange={(e) => setLoc(e.target.value)}><option value="">Select…</option>{locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</Select></Field>
        <Field label="Handover note"><TextArea data-testid="rh-note" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
        <Btn data-testid="rh-add" onClick={add}>Post handover</Btn>
      </Panel>
      {items.map((h) => <Panel key={h.id} className="p-4"><p className="text-sm text-purple-100">{h.note}</p><p className="text-xs text-purple-400 mt-1">{h.author} · {h.department_name}</p></Panel>)}
    </div>
  );
}

function Empty({ text }) { return <Panel className="p-12 text-center text-purple-400/70">{text}</Panel>; }
